import React from 'react';
import {feedbackText} from '../feedbackText';
import {
  FeedbackForm,
  MAX_ANSWERS,
  MAX_ANSWER_LENGTH,
  MAX_QUESTION_LENGTH,
  MAX_TITLE_LENGTH,
  MIN_ANSWERS,
} from '../feedbackForm';
import FeedbackPhonePreview from './FeedbackPhonePreview';
import FeedbackTriggerField from './FeedbackTriggerField';
import editorStyles from '../../Microreels/List/components/MicroreelQuestionEditor.module.css';
import styles from './FeedbackQuestionEditor.module.css';

interface Props {
  form: FeedbackForm;
  isSaving: boolean;
  canSave: boolean;
  hasDuplicates: boolean;
  onChange: (form: FeedbackForm) => void;
  onSave: () => void;
  onCancel: () => void;
}

const ANSWER_COUNTER_THRESHOLD = MAX_ANSWER_LENGTH - 30;

const FeedbackQuestionEditor = ({
  form,
  isSaving,
  canSave,
  hasDuplicates,
  onChange,
  onSave,
  onCancel,
}: Props) => {
  const {title, question, answers, trigger} = form;
  const canAddAnswer = answers.length < MAX_ANSWERS;

  const setAnswer = (index: number, value: string) => {
    onChange({
      ...form,
      answers: answers.map((answer, i) => (i === index ? value : answer)),
    });
  };

  const removeAnswer = (index: number) => {
    onChange({...form, answers: answers.filter((_, i) => i !== index)});
  };

  const addAnswer = () => {
    if (!canAddAnswer) return;
    onChange({...form, answers: [...answers, '']});
  };

  return (
    <div className={editorStyles.body}>
      <div className={editorStyles.phoneColumn}>
        <FeedbackPhonePreview
          title={title}
          question={question}
          answers={answers}
        />
        <p className={editorStyles.phoneCaption}>
          {feedbackText.showAfterCaption(trigger)}
        </p>
      </div>

      <div className={editorStyles.editorColumn}>
        <h2 className={editorStyles.title}>{feedbackText.editorTitle}</h2>
        <p className={editorStyles.subtitle}>{feedbackText.editorSubtitle}</p>

        <div className={`${editorStyles.answersScroll} ${styles.scroll}`}>
          <label className={editorStyles.fieldLabel} htmlFor="feedback-title">
            {feedbackText.titleLabel}
          </label>
          <input
            id="feedback-title"
            className={`${editorStyles.questionInput} ${styles.titleInput}`}
            value={title}
            placeholder={feedbackText.titlePlaceholder}
            maxLength={MAX_TITLE_LENGTH}
            disabled={isSaving}
            onChange={ev => onChange({...form, title: ev.target.value})}
          />
          <span className={editorStyles.questionCounter}>
            {title.length} / {MAX_TITLE_LENGTH}
          </span>

          <label
            className={editorStyles.fieldLabel}
            htmlFor="feedback-question">
            {feedbackText.questionLabel}
          </label>
          <textarea
            id="feedback-question"
            className={editorStyles.questionInput}
            value={question}
            placeholder={feedbackText.questionPlaceholder}
            rows={2}
            maxLength={MAX_QUESTION_LENGTH}
            disabled={isSaving}
            onChange={ev => onChange({...form, question: ev.target.value})}
          />
          <span className={editorStyles.questionCounter}>
            {question.length} / {MAX_QUESTION_LENGTH}
          </span>

          <p className={editorStyles.fieldLabel}>{feedbackText.answersLabel}</p>
          <p className={editorStyles.answersHint}>{feedbackText.answersHint}</p>

          <div className={editorStyles.answersList}>
            {answers.map((answer, index) => (
              <div
                key={index}
                className={`${editorStyles.answerRow} ${styles.answerRow}`}>
                <input
                  className={editorStyles.answerInput}
                  value={answer}
                  placeholder={feedbackText.answerPlaceholder}
                  maxLength={MAX_ANSWER_LENGTH}
                  disabled={isSaving}
                  aria-label={`${feedbackText.answersLabel} ${index + 1}`}
                  onChange={ev => setAnswer(index, ev.target.value)}
                />
                {answer.length >= ANSWER_COUNTER_THRESHOLD && (
                  <span className={editorStyles.answerCounter}>
                    {answer.length} / {MAX_ANSWER_LENGTH}
                  </span>
                )}
                <button
                  className={editorStyles.answerDelete}
                  onClick={() => removeAnswer(index)}
                  disabled={isSaving || answers.length <= MIN_ANSWERS}
                  title={feedbackText.deleteAnswer}>
                  <span className="icon-trash" />
                </button>
              </div>
            ))}
          </div>

          <button
            className={editorStyles.addAnswerButton}
            onClick={addAnswer}
            disabled={isSaving || !canAddAnswer}
            title={
              canAddAnswer
                ? undefined
                : feedbackText.maxAnswersHint(MAX_ANSWERS)
            }>
            {feedbackText.addAnswer}
          </button>

          <FeedbackTriggerField
            value={trigger}
            disabled={isSaving}
            onChange={value => onChange({...form, trigger: value})}
          />
        </div>

        <div className={editorStyles.footerCard}>
          <span
            className={`${editorStyles.footerSummary} ${
              hasDuplicates ? editorStyles.footerSummaryWarning : ''
            }`}>
            {hasDuplicates
              ? feedbackText.duplicateAnswersWarning
              : feedbackText.answersSummary(answers.length)}
          </span>
          <div className={editorStyles.footerActions}>
            <button
              className={editorStyles.cancelButton}
              onClick={onCancel}
              disabled={isSaving}>
              {feedbackText.cancel}
            </button>
            <button
              className={editorStyles.saveButton}
              onClick={onSave}
              disabled={!canSave}>
              {isSaving ? feedbackText.creating : feedbackText.createFeedback}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeedbackQuestionEditor;
